import { ContactNoteStatus } from 'views/ContactNoteForm/data/statusOptions';
import { ContactTypeOptions } from 'views/ContactNoteForm/components/data/contactTypeOptions';
import statusPlaceholder from 'views/ContactNoteForm/components/data/statusPlaceholder';
import { addDays } from 'date-fns';
import ContactNote, { ContactNoteDialog } from 'types/ContactNote';
import { BusinessRelationshipResponse } from 'types/BusinessRelationship';
import { isDigit, isEmpty } from 'utils/stringUtil';
import { EnvConfig } from 'config/envConfig';
import {
  ContactNoteDialogStatus,
  submissionStatusPlaceholder,
} from 'views/ContactNoteFormDialog/zafire/components/data/ContactNoteDialogStatus';

export const getRmFullName = (contactNote: ContactNote | ContactNoteDialog) => {
  return `${contactNote.partner.relationshipManager.fullName}(${contactNote.partner.relationshipManager.uid})`;
};

export const translateStatusEnum = (status: string, createdDateTime: string, tripId: string) => {
  const foundStatus = findStatusObject(status);
  if (foundStatus) {
    const remainingDays = getRemainingDays(createdDateTime, 45);
    if (EnvConfig.config.newSubmissionStatus) {
      if (
        remainingDays > 0 &&
        ![ContactNoteDialogStatus.INSUBMISSION, ContactNoteDialogStatus.SUBMITTED].includes(
          status as ContactNoteDialogStatus,
        ) &&
        isEmpty(tripId)
      ) {
        if (remainingDays.toFixed(0) === '1') {
          return `${foundStatus.label} (${remainingDays.toFixed(0)} day left)`;
        }
        return `${foundStatus.label} (${remainingDays.toFixed(0)} days left)`;
      }
    } else if (remainingDays > 0 && status !== ContactNoteStatus.SUBMITTED && isEmpty(tripId)) {
      if (remainingDays.toFixed(0) === '1') {
        return `${foundStatus.label} (${remainingDays.toFixed(0)} day left)`;
      }
      return `${foundStatus.label} (${remainingDays.toFixed(0)} days left)`;
    }
    return foundStatus.label;
  }
  return status; // Return raw status if not found in placeholder
};

export const findContactTypeObject = (nContactType: string) => {
  return ContactTypeOptions.find((contactType) => contactType.value === nContactType);
};

export const findStatusObject = (noteStatus: string) => {
  if (EnvConfig.config.newSubmissionStatus) {
    return submissionStatusPlaceholder.find((status) => status.value === noteStatus);
  }
  return statusPlaceholder.find((status) => status.value === noteStatus);
};

export const translateContactTypeEnum = (contactType: string) => {
  const foundContactType = findContactTypeObject(contactType);
  if (foundContactType) {
    return foundContactType.label;
  }
  return contactType;
};

export function getRemainingDays(createdDateTime: string, offset: number) {
  const createdDate = new Date(createdDateTime);
  const endDateTime = addDays(createdDate, offset);
  const differenceInTime = Math.round(endDateTime.getTime().valueOf() - Date.now().valueOf());
  return differenceInTime / (1000 * 3600 * 24);
}

export type TopicDropdownOption = { label: string; value: string };

export function formatTopicsLabel(topics: string[] | null | undefined, topicsOptions: TopicDropdownOption[]): string {
  if (!topics || topics.length === 0) {
    return '-';
  }

  const labels = topics.map((topic) => {
    const option = topicsOptions.find((o) => o.value === topic);
    return option?.label ?? topic;
  });

  return labels.join(', ');
}

/**
 * Format a BR master number for display (e.g. "12345678" becomes "1234.5678").
 * Falls back to "Prospecting BR" when there is no number.
 * @param brNumber the BR master number
 */
export function formatBrNumber(brNumber?: string | null): string {
  if (isDigit(brNumber?.slice(0, 8) ?? '')) {
    return `${brNumber?.slice(0, 4)}.${brNumber?.slice(4)}`;
  }
  return brNumber || 'Prospecting BR';
}

/**
 * Format the BR label shown to users: the BR number followed by the BR name, e.g. "1234.5678 | Smith Family".
 * Only the number is shown when no name is available.
 * @param brNumber the BR master number
 * @param brName the BR name
 */
export function formatBrLabel(brNumber?: string | null, brName?: string | null): string {
  const number = formatBrNumber(brNumber);
  return brName ? `${number} | ${brName}` : number;
}

/**
 * Build the related-BR dropdown option for a BR: the label shown to users, the BR uid as value and the
 * raw BR number (used to match what the user types in the search field).
 * @param br the BR as returned by the related-BRs API
 */
export const toBrOption = (br: BusinessRelationshipResponse) => ({
  label: formatBrLabel(br.brNumber, br.firstDisplayName),
  value: br.brUid,
  brNumber: br.brNumber ?? '',
  brName: br.firstDisplayName ?? '',
});

/**
 * Normalize text for the search fields: case-insensitive, and dots (as in "1234.5678") are ignored.
 */
export const normalizeSearchText = (text: string) => text.toLowerCase().replaceAll('.', '');

/**
 * Check whether an entry matches what the user typed in a search field. A `*` at the start and/or the end of the
 * text is a wildcard:
 * - `Smith*` one of the `anchors` starts with "smith"
 * - `*Trust` one of the `anchors` ends with "trust"
 * - `*Family*`, or no `*` at all, the label or one of the `anchors` contains the text
 * A text made only of `*` matches everything. All strings must already be normalized with `normalizeSearchText`.
 * @param term the text typed by the user
 * @param entry the label of the entry and the parts of it (for example a name and a number) that the `*` anchors to
 */
export const matchesWildcardSearch = (term: string, entry: { label: string; anchors: string[] }): boolean => {
  const startsWithWildcard = term.startsWith('*');
  const endsWithWildcard = term.endsWith('*');
  const text = term.replaceAll('*', '');
  if (text === '') {
    return true;
  }
  if (startsWithWildcard && !endsWithWildcard) {
    return entry.anchors.some((anchor) => anchor.endsWith(text));
  }
  if (!startsWithWildcard && endsWithWildcard) {
    return entry.anchors.some((anchor) => anchor.startsWith(text));
  }
  return entry.label.includes(text) || entry.anchors.some((anchor) => anchor.includes(text));
};

/**
 * Check whether an entry matches what the user typed in the BR and bank representative search fields. The text is found
 * anywhere in the label or one of the `anchors`, with or without a `*` on either side, so `Investment*` and
 * `*Investment` both find "Johnson Investment Account". A text made only of `*` matches everything.
 * @param term the text typed by the user, normalized
 * @param entry the normalized label of the entry and the parts of it (for example a name and a number)
 */
export const matchesContainsSearch = (term: string, entry: { label: string; anchors: string[] }): boolean => {
  const text = term.replaceAll('*', '');
  if (text === '') {
    return true;
  }
  return entry.label.includes(text) || entry.anchors.some((anchor) => anchor.includes(text));
};

/**
 * The name part of an employee dropdown label such as "Smith John (u12345)": the label without its trailing user id.
 * @param label the employee label
 */
export const employeeSearchName = (label: string) => label.replace(/\s*\([^()]*\)\s*$/, '');
